import { getCookie } from 'src/utils';

interface TrackEventParams {
    eventName: string;
    eventData?: Record<string, unknown>;
    email?: string;
    phone?: string;
    eventId?: string;
    sendServerEvent?: boolean;
}

/**
 * Generate unique event ID for Meta deduplication
 * Format: web-{timestamp}-{random}
 * This ensures each event is unique whilst allowing deduplication between pixel and CAPI
 */
export const generateMetaEventId = (): string => {
    return `web-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

/**
 * Get Facebook pixel cookies (_fbp and _fbc)
 * These are used by Meta for better event matching and attribution
 */
const getFacebookPixelData = () => {
    return {
        fbp: getCookie('_fbp') || undefined,
        fbc: getCookie('_fbc') || undefined
    };
};

/**
 * Track Meta Pixel event with hybrid Pixel + CAPI implementation
 *
 * This function implements Meta's recommended best practice for event tracking:
 * 1. Fires client-side pixel event (immediate, browser-based)
 * 2. Fires server-side CAPI event via Netlify Function
 * 3. Uses shared event_id for automatic deduplication by Meta
 *
 * Benefits of hybrid tracking:
 * - Redundancy: If one method fails, the other captures the event
 * - Better attribution: Combines browser cookies + server-side data
 * - Higher Event Match Quality: More data points for Meta's algorithm
 * - Deduplication: Event ID prevents double-counting
 *
 * @param eventName - Meta event name (e.g., 'PageView', 'ViewContent')
 * @param eventData - Optional custom data to include with the event
 * @param email - Optional email address (will be hashed server-side)
 * @param phone - Optional phone number (will be hashed server-side)
 *
 * @example
 * // Track a simple PageView
 * trackMetaEvent({ eventName: 'PageView' });
 *
 * @example
 * // Track a conversion with customer data
 * trackMetaEvent({
 *   eventName: 'Purchase',
 *   eventData: { value: 99.99, currency: 'GBP' },
 *   email: 'customer@example.com'
 * });
 */
export const trackMetaEvent = async ({
    eventName,
    eventData = {},
    email,
    phone,
    eventId,
    sendServerEvent = true
}: TrackEventParams): Promise<void> => {
    // Generate unique event ID for deduplication
    const resolvedEventId = eventId ?? generateMetaEventId();
    const { fbp, fbc } = getFacebookPixelData();

    // 1. Fire client-side pixel (immediate, non-blocking)
    if (typeof window !== 'undefined' && window.fbq) {
        try {
            window.fbq('track', eventName, eventData, { eventID: resolvedEventId });
        } catch (error) {
            console.warn('[Meta Pixel] Client-side tracking failed:', error);
        }
    }

    if (sendServerEvent) {
        // 2. Fire server-side CAPI via Netlify Function (async, best effort)
        // This runs in the background and doesn't block user experience
        try {
            await fetch('/.netlify/functions/meta-track', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                keepalive: true,
                body: JSON.stringify({
                    event_name: eventName,
                    event_id: resolvedEventId,
                    event_source_url: typeof window !== 'undefined' ? window.location.href : '',
                    email,
                    phone,
                    fbp,
                    fbc,
                    custom_data: eventData
                })
            });
        } catch (error) {
            // Don't block user experience if CAPI fails
            // Server-side tracking is a best-effort enhancement
            console.warn('[Meta CAPI] Failed to send server event:', error);
        }
    }
};

// Global type declaration for fbq
declare global {
    interface Window {
        fbq?: (action: string, event: string, data?: unknown, options?: { eventID: string }) => void;
    }
}
